// src/api/services/departmentService.ts

import { departmentEndpoints } from '@/api/endpoints/departments';
import { Department } from '@/types/department'; // Assuming this path is correct
import { useOrgApiClient } from '@/util/orgApiClient'; // Assuming this path is correct

// Define CreateDepartmentData interface (used by the create method)
// This defines the shape of data expected when creating a new department.
interface CreateDepartmentData {
  name: string;
  description?: string; // Description is optional
}

/**
 * Custom hook to provide department-related service methods.
 * These methods are scoped to the currently selected organization.
 * Throws an error if no organization is selected.
 */
export function useDepartmentService() {
  const { orgFetch, selectedOrg } = useOrgApiClient();

  // Ensure an organization is selected before providing service methods.
  // This hook should only be used in contexts where an organization is guaranteed to be selected.
  if (!selectedOrg) {
    // Throwing an error here ensures that consuming components/hooks
    // are aware that an organization context is required.
    throw new Error(
      'useDepartmentService: No organization selected. This hook requires an active organization context.',
    );
  }

  return {
    /**
     * Fetches a list of departments for the selected organization.
     * @returns {Promise<Department[]>} A promise that resolves to an array of departments.
     */
    list: () =>
      orgFetch<Department[]>(departmentEndpoints.LIST(selectedOrg.id)),

    /**
     * Fetches the details of a specific department.
     * @param {string} departmentId - The ID of the department to fetch.
     * @returns {Promise<Department>} A promise that resolves to the department details.
     */
    get: (departmentId: string) =>
      orgFetch<Department>(
        departmentEndpoints.DETAIL(selectedOrg.id, departmentId),
      ),

    /**
     * Creates a new department in the selected organization.
     * @param {CreateDepartmentData} data - The data for the new department.
     * @returns {Promise<Department>} A promise that resolves to the newly created department.
     */
    create: (data: CreateDepartmentData) =>
      orgFetch<Department>(departmentEndpoints.CREATE(selectedOrg.id), {
        method: 'POST',
        body: JSON.stringify(data),
        // Headers like 'Content-Type': 'application/json' are likely handled by orgFetch or underlying apiClient
      }),

    /**
     * Updates an existing department.
     * @param {string} departmentId - The ID of the department to update.
     * @param {Partial<Department>} data - The partial data to update the department with.
     * @returns {Promise<Department>} A promise that resolves to the updated department.
     */
    update: (departmentId: string, data: Partial<Department>) =>
      orgFetch<Department>(
        departmentEndpoints.UPDATE(selectedOrg.id, departmentId),
        {
          method: 'PATCH', // Or 'PUT' depending on your API's preference for partial/full updates
          body: JSON.stringify(data),
        },
      ),

    /**
     * Deletes a department.
     * @param {string} departmentId - The ID of the department to delete.
     * @returns {Promise<void>} A promise that resolves when the department is deleted.
     */
    delete: (departmentId: string) =>
      orgFetch<void>(departmentEndpoints.DELETE(selectedOrg.id, departmentId), {
        method: 'DELETE',
      }),
  };
}
